import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiAlertCircle, FiCheck, FiGlobe, FiLoader, FiSend } from 'react-icons/fi';
import { api } from '../services/api';
import './AdminArticleTranslationPublish.css';

const REQUIRED_FIELDS = [
  ['title', 'عنوان'],
  ['slug', 'Slug'],
  ['excerpt', 'خلاصه مقاله'],
  ['content', 'متن کامل مقاله'],
  ['category', 'دسته‌بندی'],
];

const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const AdminArticleTranslationPublish = ({ articleId, articlePublicationStatus = 'published' }) => {
  const [loading, setLoading] = useState(true);
  const [locales, setLocales] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [activeLocale, setActiveLocale] = useState('');
  const [publishState, setPublishState] = useState('idle');
  const [pendingVersion, setPendingVersion] = useState(0);
  const [error, setError] = useState('');
  const pendingFields = useRef(new Set());

  const enabledLocales = useMemo(
    () => locales.filter((locale) => locale.enabled !== false && locale.code !== 'fa'),
    [locales]
  );
  const activeLanguage = enabledLocales.find((locale) => locale.code === activeLocale);
  const activeTranslation = translations.find((item) => item.locale === activeLocale);
  const isStale = Boolean(activeTranslation?.isStale || activeTranslation?.isPublishedStale);
  const sourcePublicationStatus = activeTranslation?.sourcePublicationStatus
    || translations.find((item) => item.sourcePublicationStatus)?.sourcePublicationStatus
    || articlePublicationStatus;
  const sourceIsPublished = sourcePublicationStatus !== 'draft';
  const activePrefix = `article:${String(articleId)}:${activeLocale}:`;
  // pendingVersion intentionally participates in rendering; the actual keys live in a ref.
  const activePendingCount = pendingVersion >= 0
    ? [...pendingFields.current].filter((key) => key.startsWith(activePrefix)).length
    : 0;
  const missingFields = REQUIRED_FIELDS.filter(([field]) => !String(activeTranslation?.[field] || '').trim());

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!articleId) return;
    if (!quiet) setLoading(true);
    try {
      const [localeResponse, translationResponse] = await Promise.all([
        api.get('/api/locales/admin'),
        api.get(`/api/articles/${articleId}/translations`),
      ]);
      const localeItems = Array.isArray(localeResponse.data) ? localeResponse.data : [];
      const translationItems = Array.isArray(translationResponse.data) ? translationResponse.data : [];
      const available = localeItems.filter((locale) => locale.enabled !== false && locale.code !== 'fa');
      setLocales(localeItems);
      setTranslations(translationItems);
      setActiveLocale((current) => (
        available.some((locale) => locale.code === current) ? current : (available[0]?.code || '')
      ));
    } catch (loadError) {
      setError(loadError.data?.message || loadError.message || 'دریافت وضعیت ترجمه‌های مقاله انجام نشد');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [articleId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const syncPendingFromWindow = () => {
      const prefix = `article:${String(articleId)}:`;
      const waiting = Object.entries(window.__haryadTranslationSaveStates || {})
        .filter(([key, state]) => key.startsWith(prefix) && ['waiting', 'saving'].includes(state));
      pendingFields.current = new Set(waiting.map(([key]) => key));
      setPendingVersion((value) => value + 1);
    };

    syncPendingFromWindow();

    const onSaveState = (event) => {
      const detail = event.detail || {};
      if (detail.entityType !== 'article' || String(detail.entityId) !== String(articleId)) return;
      const key = `${detail.entityType}:${String(articleId)}:${detail.locale || ''}:${detail.fieldPath || ''}`;
      if (['waiting', 'saving'].includes(detail.state)) pendingFields.current.add(key);
      else pendingFields.current.delete(key);
      setPendingVersion((value) => value + 1);
    };
    const onTranslationsChanged = (event) => {
      const detail = event.detail || {};
      if (detail.entityType === 'article' && String(detail.entityId) === String(articleId)) {
        load({ quiet: true });
      }
    };
    window.addEventListener('haryad:translation-save-state', onSaveState);
    window.addEventListener('haryad:translations-changed', onTranslationsChanged);
    return () => {
      window.removeEventListener('haryad:translation-save-state', onSaveState);
      window.removeEventListener('haryad:translations-changed', onTranslationsChanged);
    };
  }, [articleId, load]);

  const flushAndWait = async (localeCode) => {
    window.dispatchEvent(new CustomEvent('haryad:flush-translations', {
      detail: { entityType: 'article', entityId: String(articleId), locale: localeCode },
    }));

    const prefix = `article:${String(articleId)}:${localeCode}:`;
    const startedAt = Date.now();
    while (Date.now() - startedAt < 12000) {
      const states = Object.entries(window.__haryadTranslationSaveStates || {})
        .filter(([key]) => key.startsWith(prefix))
        .map(([, state]) => state);
      if (!states.some((state) => ['waiting', 'saving'].includes(state))) {
        if (states.some((state) => state === 'error')) {
          throw new Error('حداقل یکی از فیلدهای ترجمه ذخیره نشده است. خطای همان فیلد را برطرف کنید و دوباره انتشار را بزنید.');
        }
        return;
      }
      await delay(90);
    }
    throw new Error('ذخیره خودکار ترجمه بیش از حد طول کشید. اتصال را بررسی کنید و دوباره تلاش کنید.');
  };

  const publish = async () => {
    if (!activeLocale || publishState === 'publishing' || !sourceIsPublished) return;
    setPublishState('publishing');
    setError('');
    try {
      // Same architecture as products: field editors only maintain a draft.
      // The one final action first flushes every draft field for this locale,
      // then creates a fresh immutable publishedSnapshot in the backend.
      await flushAndWait(activeLocale);

      const latestResponse = await api.get(`/api/articles/${articleId}/translations`);
      const latestItems = Array.isArray(latestResponse.data) ? latestResponse.data : [];
      const translation = latestItems.find((item) => item.locale === activeLocale);
      if (!translation) throw new Error('ابتدا دست‌کم یک فیلد این زبان را ترجمه و ذخیره کنید');

      const latestMissing = REQUIRED_FIELDS.filter(([field]) => !String(translation[field] || '').trim());
      if (latestMissing.length) {
        throw new Error(`برای انتشار این زبان ابتدا این بخش‌ها را کامل کنید: ${latestMissing.map(([, label]) => label).join('، ')}`);
      }

      await api.put(`/api/articles/${articleId}/translations/${activeLocale}`, {
        ...translation,
        status: 'published',
      });

      // Verify the exact public lookup used by ArticleDetail. A green success state
      // therefore means the locale can really open and LocaleContext can stay on it.
      await api.get(`/api/articles/${articleId}?locale=${encodeURIComponent(activeLocale)}`);

      await load({ quiet: true });
      setPublishState('published');
      window.dispatchEvent(new CustomEvent('haryad:translations-changed', {
        detail: { entityType: 'article', entityId: articleId },
      }));
    } catch (publishError) {
      setPublishState('error');
      const missing = Array.isArray(publishError.data?.missingPaths)
        ? ` (${publishError.data.missingPaths.join('، ')})`
        : '';
      setError(`${publishError.data?.message || publishError.message || 'انتشار ترجمه مقاله انجام نشد'}${missing}`);
    }
  };

  if (!articleId) return null;

  return (
    <section className="article-editor-translation-publish" aria-label="انتشار نهایی ترجمه مقاله">
      <div className="article-editor-translation-publish__heading">
        <span><FiGlobe /></span>
        <div>
          <strong>انتشار ترجمهٔ مقاله</strong>
          <small>همهٔ فیلدها پیش‌نویس را ذخیره می‌کنند؛ برای هر زبان فقط همین دکمه انتشار وجود دارد.</small>
        </div>
      </div>

      {loading ? (
        <div className="article-editor-translation-publish__loading"><FiLoader /> در حال دریافت...</div>
      ) : (
        <>
          <div className="article-editor-translation-publish__locales" role="tablist" aria-label="زبان ترجمه مقاله">
            {enabledLocales.map((locale) => {
              const translation = translations.find((item) => item.locale === locale.code);
              const stale = Boolean(translation?.isStale || translation?.isPublishedStale);
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeLocale === locale.code}
                  className={`${activeLocale === locale.code ? 'is-active' : ''} ${stale ? 'is-stale' : ''}`}
                  onClick={() => {
                    setActiveLocale(locale.code);
                    setPublishState('idle');
                    setError('');
                  }}
                  key={locale.code}
                >
                  <b>{locale.code.toUpperCase()}</b>
                  {locale.nativeName}
                  {translation?.status === 'published' ? <FiCheck /> : null}
                  {stale ? <small>نیاز به بازبینی</small> : null}
                </button>
              );
            })}
          </div>

          <div className="article-editor-translation-publish__status">
            {!sourceIsPublished ? <><FiAlertCircle /> مقالهٔ اصلی هنوز پیش‌نویس است؛ ابتدا وضعیت انتشار مقاله را «منتشر شده» کنید و تغییرات مقاله را ذخیره کنید.</> : null}
            {sourceIsPublished && activePendingCount ? <><FiLoader className="is-spinning" /> {activePendingCount} بخش این زبان در حال ذخیره خودکار است؛ با انتشار نهایی، ذخیره‌ها ابتدا کامل می‌شوند.</> : null}
            {sourceIsPublished && !activePendingCount && activeTranslation && missingFields.length ? <><FiAlertCircle /> برای انتشار: {missingFields.map(([, label]) => label).join('، ')}</> : null}
            {sourceIsPublished && !activePendingCount && !missingFields.length && isStale ? <><FiAlertCircle /> نسخهٔ منتشرشده روی سایت باقی می‌ماند؛ پس از بازبینی دوباره منتشر کنید.</> : null}
            {sourceIsPublished && !activePendingCount && !missingFields.length && !isStale && activeTranslation?.hasUnpublishedChanges ? 'تغییرات این زبان در پیش‌نویس ذخیره شده‌اند.' : null}
            {sourceIsPublished && !activePendingCount && !missingFields.length && !isStale && activeTranslation?.status === 'published' && !activeTranslation?.hasUnpublishedChanges ? 'نسخهٔ منتشرشدهٔ این زبان به‌روز است.' : null}
            {sourceIsPublished && !activePendingCount && !activeTranslation ? 'هنوز پیش‌نویسی برای این زبان ثبت نشده است.' : null}
            {error ? <strong role="alert">{error}</strong> : null}
          </div>

          <button
            type="button"
            className={`article-editor-translation-publish__button ${publishState === 'publishing' ? 'is-publishing' : ''}`}
            onClick={publish}
            disabled={!sourceIsPublished || !activeLanguage || publishState === 'publishing'}
          >
            {publishState === 'publishing' ? <FiLoader className="is-spinning" /> : <FiSend />}
            {publishState === 'published'
              ? `ترجمهٔ ${activeLanguage?.nativeName || activeLocale} منتشر شد`
              : `انتشار نهایی ترجمهٔ ${activeLanguage?.nativeName || activeLocale}`}
          </button>
        </>
      )}
    </section>
  );
};

export default AdminArticleTranslationPublish;
